import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Empty, Form, Input, Modal, Row, Select, Space, Spin, Statistic, Table, Tag, Typography } from 'antd';
import { BarChartOutlined, InboxOutlined, KeyOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../shared/i18n';
import { userPath } from '../../../shared/config/paths';
import PageHeader from '../../../shared/components/PageHeader';
import { formatMoney } from '../../../shared/utils/money';
import { CopyLine, bucketNameError, fmtBytes, fmtCount, fmtDate, listOf, s3, toastErr } from './common';

const { Text } = Typography;

export const CreateBucketModal = ({ open, regions, onClose, onCreated }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) form.setFieldsValue({ region_id: regions?.[0]?.id, name: '' });
    }, [open, regions, form]);

    const submit = async () => {
        const v = await form.validateFields();
        setSaving(true);
        try {
            const d = await s3.post('/buckets', { region_id: v.region_id, name: v.name.trim() });
            onCreated?.(d);
        } catch (e) {
            toastErr(e, t('Failed to create bucket'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open={open} title={t('Create bucket')} onCancel={onClose} onOk={submit} confirmLoading={saving}
            okText={t('Create')} destroyOnHidden>
            <Form form={form} layout="vertical" requiredMark={false}>
                <Form.Item name="region_id" label={t('Region')} rules={[{ required: true }]}>
                    <Select options={(regions || []).map((r) => ({
                        value: r.id,
                        label: <Space><span>{r.name}</span><Text type="secondary">{r.endpoint}</Text></Space>,
                    }))} />
                </Form.Item>
                <Form.Item name="name" label={t('Bucket name')}
                    normalize={(v) => (v || '').toLowerCase()}
                    rules={[{ validator: (_, v) => { const err = bucketNameError((v || '').trim()); return err ? Promise.reject(new Error(err)) : Promise.resolve(); } }]}>
                    <Input placeholder="my-bucket" maxLength={63} autoFocus />
                </Form.Item>
            </Form>
        </Modal>
    );
};

const S3Overview = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [ov, setOv] = useState(null);
    const [buckets, setBuckets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [o, b] = await Promise.all([s3.get('/overview'), s3.get('/buckets').catch(() => [])]);
            setOv(o || {});
            setBuckets(listOf(b));
        } catch (e) {
            toastErr(e, t('Failed to load'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const regions = Array.isArray(ov?.regions) ? ov.regions : [];
    const available = ov && ov.enabled !== false && regions.length > 0;

    const cols = [
        { title: t('Name'), dataIndex: 'name', render: (v, b) => <Link to={userPath(`/s3/buckets/${b.id}`)}>{v}</Link> },
        { title: t('Region'), dataIndex: 'region_name', width: 140, render: (v, b) => v || b.region || '-' },
        { title: t('Size'), dataIndex: 'bytes', width: 120, render: fmtBytes, sorter: (a, b) => (a.bytes || 0) - (b.bytes || 0) },
        { title: t('Objects'), dataIndex: 'objects', width: 110, render: fmtCount },
        {
            title: t('Access'), dataIndex: 'public_access', width: 100,
            render: (v) => (v ? <Tag color="orange" style={{ margin: 0 }}>{t('Public')}</Tag> : <Tag style={{ margin: 0 }}>{t('Private')}</Tag>),
        },
        { title: t('Created'), dataIndex: 'created_at', width: 180, render: fmtDate },
    ];

    if (!loading && ov && !available) {
        return (
            <div>
                <PageHeader title={t('Object Storage')} icon={<InboxOutlined />} />
                <Card><Empty description={t('Object Storage is not available yet')} /></Card>
            </div>
        );
    }

    return (
        <div>
            <PageHeader title={t('Object Storage')} icon={<InboxOutlined />}
                extra={<Space wrap>
                    <Button icon={<ReloadOutlined />} onClick={load} loading={loading} />
                    <Button icon={<KeyOutlined />} onClick={() => navigate(userPath('/s3/keys'))}>{t('Access keys')}</Button>
                    <Button icon={<BarChartOutlined />} onClick={() => navigate(userPath('/s3/usage'))}>{t('Usage')}</Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)} disabled={!available}>{t('Create bucket')}</Button>
                </Space>} />

            {ov?.hold && (
                <Alert type="error" showIcon style={{ marginBottom: 16 }}
                    message={ov.hold.purged ? t('Your data was deleted because an invoice stayed unpaid')
                        : t('Suspended for an overdue invoice: access keys and public access are off, your data is kept')}
                    description={!ov.hold.purged && ov.hold.delete_at ? t('Data will be deleted on {{date}} if still unpaid', { date: fmtDate(ov.hold.delete_at) }) : null}
                    action={!ov.hold.purged && (
                        <Button size="small" type="primary" danger
                            onClick={() => navigate(userPath(ov.hold.invoice_id ? `/billing/invoices/${ov.hold.invoice_id}` : '/billing/invoices'))}>
                            {t('Pay invoice')}
                        </Button>
                    )} />
            )}
            <Spin spinning={loading && !ov}>
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    <Col xs={12} md={6}><Card><Statistic title={t('Buckets')} value={ov?.buckets ?? buckets.length} /></Card></Col>
                    <Col xs={12} md={6}><Card><Statistic title={t('Stored')} value={fmtBytes(ov?.stored_bytes)} /></Card></Col>
                    <Col xs={12} md={6}><Card><Statistic title={t('Access keys')} value={ov?.keys ?? 0} /></Card></Col>
                    <Col xs={12} md={6}>
                        <Card>
                            <Statistic title={<Link to={userPath('/s3/usage')}>{t('This month (estimate)')}</Link>}
                                value={formatMoney(ov?.usage?.estimate ?? 0, 'USD')} />
                        </Card>
                    </Col>
                </Row>

                <Row gutter={[16, 16]}>
                    <Col xs={24} xl={17}>
                        <Card title={t('Buckets')} styles={{ body: { padding: 0 } }}>
                            <Table rowKey="id" columns={cols} dataSource={buckets} loading={loading} size="middle"
                                pagination={buckets.length > 20 ? { pageSize: 20 } : false} scroll={{ x: 760 }}
                                onRow={(b) => ({ onDoubleClick: () => navigate(userPath(`/s3/buckets/${b.id}`)) })}
                                locale={{
                                    emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No buckets yet')}>
                                        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>{t('Create bucket')}</Button>
                                    </Empty>,
                                }} />
                        </Card>
                    </Col>
                    <Col xs={24} xl={7}>
                        <Card title={t('S3 endpoints')} size="small">
                            <Space direction="vertical" style={{ width: '100%' }} size={10}>
                                {regions.map((r) => (
                                    <div key={r.id}>
                                        <Space size={6}><Text strong>{r.name}</Text><Tag style={{ margin: 0 }}>{r.code}</Tag></Space>
                                        <div><CopyLine value={r.endpoint} /></div>
                                    </div>
                                ))}
                            </Space>
                        </Card>
                    </Col>
                </Row>
            </Spin>

            <CreateBucketModal open={createOpen} regions={regions} onClose={() => setCreateOpen(false)}
                onCreated={(d) => { setCreateOpen(false); if (d?.id) navigate(userPath(`/s3/buckets/${d.id}`)); else load(); }} />
        </div>
    );
};

export default S3Overview;
